import googleConfig from "external/source/google/config";

export enum Scope {
  AdminDirectoryCustomerReadonly = "https://www.googleapis.com/auth/admin.directory.customer.readonly",
  AdminDirectoryGroup = "https://www.googleapis.com/auth/admin.directory.group",
  AdminDirectoryGroupReadOnly = "https://www.googleapis.com/auth/admin.directory.group.readonly",
  AdminDirectoryUser = "https://www.googleapis.com/auth/admin.directory.user",
  AdminDirectoryUserReadOnly = "https://www.googleapis.com/auth/admin.directory.user.readonly",
  AdminReportsUsageReadOnly = "https://www.googleapis.com/auth/admin.reports.usage.readonly",
}

// Extracted and sometimes modified from
// https://developers.google.com/identity/protocols/oauth2/scopes
// Modifications are:
// * remove "the provisioning of" from non-readonly scopes
export const ScopeDescriptions: { [s in Scope]: string } = {
  [Scope.AdminDirectoryCustomerReadonly]:
    "View Workspace customer related information",
  [Scope.AdminDirectoryGroup]: "View and manage groups on your domain",
  [Scope.AdminDirectoryGroupReadOnly]: "View groups on your domain",
  [Scope.AdminDirectoryUser]: "View and manage users on your domain",
  [Scope.AdminDirectoryUserReadOnly]: "View users on your domain",
  [Scope.AdminReportsUsageReadOnly]: "View usage reports for your domain",
};

// try to:
// * keep this in alphabetical order
export const ReadWriteScopes = [
  Scope.AdminDirectoryCustomerReadonly,
  Scope.AdminDirectoryGroup,
  Scope.AdminDirectoryUser,
  Scope.AdminReportsUsageReadOnly,
];

export const ReadOnlyScopes = [
  Scope.AdminDirectoryCustomerReadonly,
  Scope.AdminDirectoryGroupReadOnly,
  Scope.AdminDirectoryUserReadOnly,
  Scope.AdminReportsUsageReadOnly,
];

export function getOAuthURL(orgUUID: string, readOnly: boolean): string {
  const params = new URLSearchParams({
    access_type: "offline",
    approval_prompt: "force",
    client_id: googleConfig.clientID ?? "",
    redirect_uri: encodeURI(
      `${window.location.origin}/environment/sources/google/oauth2`
    ),
    response_type: "code",
    scope: (readOnly ? ReadOnlyScopes : ReadWriteScopes).join(" "),
    state: orgUUID,
  });
  return `https://accounts.google.com/o/oauth2/auth?${params.toString()}`;
}

export interface ScopesEvaluation {
  summary: ScopesEvaluationSummary;
  breakdown: { [scope: string]: boolean };
}

export enum ScopesEvaluationSummary {
  Insufficient,
  ReadOnly,
  ReadOnlyPartial,
  ReadWrite,
  ReadWritePartial,
}

export function evaluateScopes(scopes: string[]): ScopesEvaluation {
  const scopeSet = new Set(scopes);
  let summary: ScopesEvaluationSummary;
  const roBreakdown: { [scope: string]: boolean } = {};
  ReadOnlyScopes.forEach((scope) => {
    roBreakdown[scope] = scopeSet.has(scope);
  });
  const rwBreakdown: { [scope: string]: boolean } = {};
  ReadWriteScopes.forEach((scope) => {
    rwBreakdown[scope] = scopeSet.has(scope);
  });
  // check if we have every scope in a category
  if (ReadWriteScopes.every((s) => rwBreakdown[s])) {
    summary = ScopesEvaluationSummary.ReadWrite;
  } else if (ReadOnlyScopes.every((s) => roBreakdown[s])) {
    summary = ScopesEvaluationSummary.ReadOnly;
  } else {
    // check if there's partial membership - sans AdminDirectoryCustomerReadonly - in the RO or RW scope set
    scopeSet.delete(Scope.AdminDirectoryCustomerReadonly);
    if (ReadWriteScopes.some((s) => rwBreakdown[s])) {
      summary = ScopesEvaluationSummary.ReadWritePartial;
    } else if (ReadOnlyScopes.some((s) => roBreakdown[s])) {
      summary = ScopesEvaluationSummary.ReadOnlyPartial;
    } else {
      summary = ScopesEvaluationSummary.Insufficient;
    }
  }
  const breakdown = { ...roBreakdown, ...rwBreakdown };
  return { summary, breakdown };
}
